import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { hydrateCurrency } from './utils/currency';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import { fetchAndStorePermissions, canView, moduleForPath } from './utils/permissions';
import FeeStructureTable from './components/FeeStructureTable';
import StatementView from './components/StatementView';
import LearnerBalances from './components/LearnerBalances';
import VatReturn from './components/VatReturn';
import AgedReceivables from './components/AgedReceivables';
import CollectionsReport from './components/CollectionsReport';
import CashFlow from './components/CashFlow';
import BankReconciliation from './components/BankReconciliation';
import CreditNoteForm from './components/CreditNoteForm';
import PostingPeriod from './components/PostingPeriod';
import BudgetEntry from './components/BudgetEntry';
import BudgetVsActual from './components/BudgetVsActual';
import SupplierCreditNoteForm from './components/SupplierCreditNoteForm';
import WhtReport from './components/WhtReport';
import YearEndClose from './components/YearEndClose';
import FinanceShell from './components/FinanceShell';
import LearnersList from './components/LearnersList';
import PaymentUpload from './components/PaymentUpload';
import BillingForm from './components/BillingForm';
import PaymentReceiptForm from './components/PaymentReceiptForm';
import Dashboard from './components/Dashboard';
import InvoicesList from './components/InvoicesList';
import ReceiptsList from './components/ReceiptsList';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import BankList from './components/BankList';
import GLAccountList from './components/GLAccountList';
import GeneralJournal from './components/GeneralJournal';
import DimensionsList from './components/DimensionsList';
import DimensionAnalysis from './components/DimensionAnalysis';
import PnLByDimension from './components/PnLByDimension';
import TrialBalance from './components/TrialBalance';
import IncomeStatement from './components/IncomeStatement';
import FindEntries from './components/FindEntries';
import BalanceSheet from './components/BalanceSheet';
import BankLedger from './components/BankLedger';
import GLEntries from './components/GLEntries';
import LearnerLedger from './components/LearnerLedger';
import FinanceDashboard from './components/FinanceDashboard';
import SetupsList from './components/SetupsList';
import NumberSeriesList from './components/NumberSeriesList';
import SupplierList from './components/SupplierList';
import SupplierLedger from './components/SupplierLedger';
import PayablesDashboard from './components/PayablesDashboard';
import ItemList from './components/ItemList';
import PurchaseQuoteList from './components/PurchaseQuoteList';
import PurchaseQuoteForm from './components/PurchaseQuoteForm';
import PurchaseRequisitionList from './components/PurchaseRequisitionList';
import PurchaseOrderList from './components/PurchaseOrderList';
import PurchaseOrderForm from './components/PurchaseOrderForm';
import PurchaseOrderPrint from './components/PurchaseOrderPrint';
import PurchaseReceiptList from './components/PurchaseReceiptList';
import VATReturnReport from './components/VATReturnReport';
import InventoryValuation from './components/InventoryValuation';
import InventoryAging from './components/InventoryAging';
import InventoryReorder from './components/InventoryReorder';
import PayablesAging from './components/PayablesAging';
import PurchaseInvoiceList from './components/PurchaseInvoiceList';
import PurchaseInvoiceForm from './components/PurchaseInvoiceForm';
import PaymentVoucherList from './components/PaymentVoucherList';
import PaymentVoucherForm from './components/PaymentVoucherForm';
import ItemLedgerEntries from './components/ItemLedgerEntries';
import StoreRequisitionList from './components/StoreRequisitionList';
import StoreRequisitionForm from './components/StoreRequisitionForm';
import ProcurementDashboard from './components/ProcurementDashboard';
import TransferOrderList from './components/TransferOrderList';
import TransferOrderForm from './components/TransferOrderForm';
import LocationList from './components/LocationList';
// Fleet
import FleetDashboard from './components/FleetDashboard';
import BusStageList from './components/BusStageList';
import RouteSetup from './components/RouteSetup';
import TransportTimeSetupPage from './components/TransportTimeSetupPage';
import TransportSetupPage from './components/TransportSetupPage';
import BusList from './components/BusList';
import TransportGroupingBoard from './components/TransportGroupingBoard';
import BusManifest from './components/BusManifest';
import LearnerRoutes from './components/LearnerRoutes';
// Library
import LibraryDashboard from './components/LibraryDashboard';
import StorageAreaList from './components/StorageAreaList';
import LibraryCatalog from './components/LibraryCatalog';
import BookLoansPage from './components/BookLoansPage';
import OverdueBooks from './components/OverdueBooks';
import BulkTextbookAssign from './components/BulkTextbookAssign';
import ItemJournalList from './components/ItemJournalList';
import ItemJournalForm from './components/ItemJournalForm';
import FixedAssetList from './components/FixedAssetList';
import FAPostingGroupList from './components/FAPostingGroupList';
import FALedgerEntries from './components/FALedgerEntries';
import FAClassList from './components/FAClassList';
import AcademicsDashboard from './components/AcademicsDashboard';
import EventsList from './pages/EventsList';
import ParentLayout from './components/ParentLayout';
import StaffLayout from './components/StaffLayout';
import StaffDashboard from './pages/StaffDashboard';
import StaffLeave from './pages/StaffLeave';
import StaffRequisitions from './pages/StaffRequisitions';
import StaffTimetable from './pages/StaffTimetable';
import StaffTeaching from './pages/StaffTeaching';
import StaffComingSoon from './pages/StaffComingSoon';
import SubjectTeacherList from './components/SubjectTeacherList';
import AcademicYearList from './components/AcademicYearList';
import StageList from './components/StageList';
import LearningAreaList from './components/LearningAreaList';
import FeeTemplateList from './components/FeeTemplateList';
import StageLearningAreaList from './components/StageLearningAreaList';
import TimetableSetup from './components/TimetableSetup';
import TeacherAllocations from './components/TeacherAllocations';
import TimetableGrid from './components/TimetableGrid';
import TimetablePrint from './components/TimetablePrint';
import StreamList from './components/StreamList';
import ClassList from './components/ClassList';
import ChargeItemList from './components/ChargeItemList';
import PostingSetupPage from './components/PostingSetupPage';
import PostingGroupsPage from './components/PostingGroupsPage';
import BatchReceiptList from './components/BatchReceiptList';
import BatchReceiptForm from './components/BatchReceiptForm';
import SupplyChainDashboard from './components/SupplyChainDashboard';
import HRDashboard from './components/HRDashboard';
import AppraisalList from './components/AppraisalList';
import AppraisalSetup from './components/AppraisalSetup';
import AppraisalReports from './components/AppraisalReports';
import DisciplinaryList from './components/DisciplinaryList';
import DisciplinaryReports from './components/DisciplinaryReports';
import ExitList from './components/ExitList';
import ExitReports from './components/ExitReports';
import CompanyDetailsPage from './components/CompanyDetailsPage';
import MailSetupPage from './components/MailSetupPage';
import PayrollDashboard from './components/PayrollDashboard';
import LearnerPostingGroupList from './components/LearnerPostingGroupList';
import VATPostingGroupList from './components/VATPostingGroupList';
import PeriodicActivitiesPage from './components/PeriodicActivitiesPage';
import EmployeeList from './components/EmployeeList';
import EmployeeCard from './components/EmployeeCard';
import PaymentMethodList from './components/PaymentMethodList';
import DepartmentList from './components/DepartmentList';
import PayGradeList   from './components/PayGradeList';
import PayGroupList   from './components/PayGroupList';
import GradingScalePage from './components/GradingScalePage';
import GradingSystemsPage from './components/GradingSystemsPage';
import LeaveDashboard        from './components/LeaveDashboard';
import LeaveTypeList         from './components/LeaveTypeList';
import LeaveGroupList        from './components/LeaveGroupList';
import LeaveApplicationList  from './components/LeaveApplicationList';
import LeaveApplicationForm  from './components/LeaveApplicationForm';
import LeaveLedgerPage       from './components/LeaveLedgerPage';
import LeaveStatementPage    from './components/LeaveStatementPage';
import LeaveReportPage       from './components/LeaveReportPage';
import AssessmentSetupList from './components/AssessmentSetupList';
import ScoreEntryPage from './components/ScoreEntryPage';
import AssessmentReportPage from './components/AssessmentReportPage';
import MeritList from './components/MeritList';
import ClassReportForms from './components/ClassReportForms';
import ClassTeacherCentre from './components/ClassTeacherCentre';
import PathwayInsight from './components/PathwayInsight';
import LernAlytics from './components/LernAlytics';
import CurrencyList        from './components/CurrencyList';
import PayrollSetupPage    from './components/PayrollSetupPage';
import TransactionCodeList from './components/TransactionCodeList';
import PayrollPeriodList   from './components/PayrollPeriodList';
import PayrollRunList      from './components/PayrollRunList';
import PayrollRunCard      from './components/PayrollRunCard';
import PayslipView         from './components/PayslipView';
import PayrollReportPage      from './components/PayrollReportPage';
import PayrollEmployeeList    from './components/PayrollEmployeeList';
import LeaveEmployeeList      from './components/LeaveEmployeeList';
import UserList           from './components/UserList';
import UserGroupList      from './components/UserGroupList';
import PermissionSetList  from './components/PermissionSetList';
import DataImportPage     from './components/DataImportPage';
import ApprovalWorkflowSetup from './components/ApprovalWorkflowSetup';
import ApprovalsInbox    from './components/ApprovalsInbox';
import Landing            from './pages/Landing';
import Register          from './pages/Register';
import VerifyEmail       from './pages/VerifyEmail';
import ForgotPassword    from './pages/ForgotPassword';
import CreateSchool      from './pages/CreateSchool';
import SchoolPending     from './pages/SchoolPending';
import SchoolsAdmin      from './pages/SchoolsAdmin';
import ParentDashboard   from './pages/ParentDashboard';
import ParentLearnerProfile from './pages/ParentLearnerProfile';
import ParentFees        from './pages/ParentFees';
import ParentStatements  from './pages/ParentStatements';
import ParentAcademics   from './pages/ParentAcademics';
import ParentTransport   from './pages/ParentTransport';
import ParentBooks       from './pages/ParentBooks';
import ParentAssessments from './pages/ParentAssessments';
import ParentLernAlytics from './pages/ParentLernAlytics';
import ParentClassTeachers from './pages/ParentClassTeachers';
import ParentMessaging   from './pages/ParentMessaging';
import SchoolInquiryForm from './pages/SchoolInquiryForm';
import AdminSchoolInquiries from './pages/AdminSchoolInquiries';

const PrivateRoute = ({ element }) => {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (!user) return <Navigate to="/login" />;
  if (user.isSuperAdmin) return element;                       // Dataport admin manages via /admin/schools
  // No school yet — wait for an admin to assign one (or create your own)
  if (!user.hasSchool) return <Navigate to="/school-pending" />;
  // A school administrator can access every module for their assigned school,
  // regardless of provisioning state.
  if (user.role === 'admin') return element;
  // Other roles: school must be fully provisioned (its own DB ready) before use
  const school = JSON.parse(localStorage.getItem('school') || 'null');
  if (!school || school.provisionStatus !== 'Active') return <Navigate to="/school-pending" />;
  // Class teachers are read-only across the app via their Role Centre, but may
  // also reach the assessment setup (rubrics), score entry (marks) and the
  // read-only assessment/merit reports for their class. The backend scopes each
  // of these to the classes/stages they are assigned to.
  // Assessment / teaching pages that any assigned teacher may reach. The backend
  // scopes every write on these to the teacher's own classes/subjects, so opening
  // the route leaks nothing — an unrelated user just sees an empty teaching scope.
  const isTeachingPage = (p) =>
    p.startsWith('/academics/assessment-setup') ||
    p.startsWith('/academics/score-entry') ||
    p.startsWith('/academics/assessment-report') ||
    p.startsWith('/academics/merit-list') ||
    p.startsWith('/academics/report-forms') ||
    p.startsWith('/academics/lernalytics');
  if (user.role === 'classTeacher') {
    const p = location.pathname;
    // Class teachers do NOT get the main dashboard — their home is the Class
    // Teacher Centre; any disallowed path (including /dashboard) redirects there.
    const allowed =
      p === '/class-teacher-centre' ||
      isTeachingPage(p) || p.startsWith('/academics/events');
    if (allowed) return element;
    return <Navigate to="/class-teacher-centre" />;
  }
  // Staff users only reach modules their PermissionSets/UserGroup actually grant —
  // mirrors the permit() checks the backend already enforces per module. A subject
  // teacher whose coarse role is `user` (assigned via Subject Teachers) may still
  // reach the assessment/teaching pages for the subjects they're assigned, even
  // without the academics module — the backend scopes those pages to their own
  // classes/subjects.
  const requiredModule = moduleForPath(location.pathname);
  if (requiredModule && !canView(requiredModule)) {
    if (user.role === 'user' && isTeachingPage(location.pathname)) return element;
    return <Navigate to="/dashboard" />;
  }
  return element;
};

// Requires login, but NOT a provisioned school (onboarding / pending / admin pages)
const AuthedRoute = ({ element }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return user ? element : <Navigate to="/login" />;
};

// Dataport platform pages — super-admin only; everyone else goes to their school
const SuperAdminRoute = ({ element }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (!user) return <Navigate to="/login" />;
  if (!user.isSuperAdmin) return <Navigate to="/dashboard" />;
  return element;
};

// Parent portal pages — parents only, wrapped in the portal layout
// (top bar with account menu + responsive side navigation)
const ParentRoute = ({ element }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'parent') return <Navigate to="/dashboard" />;
  return <ParentLayout>{element}</ParentLayout>;
};

// Staff Portal pages — the default self-service role centre for employees.
// Open to any authenticated employee role (user / admin / classTeacher) whose
// school is provisioned; parents and super-admins have their own homes.
const StaffRoute = ({ element }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (!user) return <Navigate to="/login" />;
  if (user.isSuperAdmin) return <Navigate to="/admin/schools" />;
  if (user.role === 'parent') return <Navigate to="/parent" />;
  if (!user.hasSchool) return <Navigate to="/school-pending" />;
  const school = JSON.parse(localStorage.getItem('school') || 'null');
  if (user.role !== 'admin' && (!school || school.provisionStatus !== 'Active')) {
    return <Navigate to="/school-pending" />;
  }
  return <StaffLayout>{element}</StaffLayout>;
};

function App() {
  useInactivityTimer();

  useEffect(() => {
    // ── Axios interceptor for 401 (token expired or invalid) ───────────────────
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) {
          // Token is invalid or expired; clear session and redirect to login
          ['token', 'user', 'school', 'permissions'].forEach(k => localStorage.removeItem(k));
          window.location.replace('/login');
        }
        return Promise.reject(err);
      }
    );

    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    // Currency lives in the client database — fetch it for anyone with an active
    // client connection (real school admins, or a super-admin impersonating one).
    // Parents are permission-gated out of setups, so skip the call for them.
    if (token && user && user.hasSchool && user.role !== 'parent') {
      axios.get(`${process.env.REACT_APP_API_URL}/api/currencies/local`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => hydrateCurrency(r.data)).catch(() => {});
    }
    // Keep module gating fresh: re-fetch effective permissions on every app
    // load (stale-while-revalidate — the cached copy answers immediately, the
    // fetch updates it). Without this, grants an admin changes mid-session
    // would only apply after the user logs out and back in.
    if (token && user && user.role === 'user') {
      fetchAndStorePermissions(token);
    }

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  return (
    <Router>
      <FinanceShell>
      <Routes>
        <Route path="/login"            element={<Login />} />
        <Route path="/register"         element={<Register />} />
        <Route path="/verify-email"     element={<VerifyEmail />} />
        <Route path="/forgot-password"  element={<ForgotPassword />} />
        <Route path="/change-password"  element={<ChangePassword />} />
        <Route path="/create-school"    element={<AuthedRoute element={<CreateSchool />} />} />
        <Route path="/school-inquiry"   element={<AuthedRoute element={<SchoolInquiryForm />} />} />
        <Route path="/school-pending"   element={<AuthedRoute element={<SchoolPending />} />} />
        <Route path="/admin"            element={<SuperAdminRoute element={<SchoolsAdmin />} />} />
        <Route path="/admin/schools"    element={<SuperAdminRoute element={<SchoolsAdmin />} />} />
        <Route path="/admin/school-inquiries" element={<SuperAdminRoute element={<AdminSchoolInquiries />} />} />
        <Route path="/dashboard" element={<PrivateRoute element={<Dashboard />} />} />

        {/* Fleet Management */}
        <Route path="/fleet" element={<PrivateRoute element={<FleetDashboard />} />} />
        <Route path="/fleet/buses" element={<PrivateRoute element={<BusList />} />} />
        <Route path="/fleet/stages" element={<PrivateRoute element={<BusStageList />} />} />
        <Route path="/fleet/routes" element={<PrivateRoute element={<RouteSetup />} />} />
        <Route path="/fleet/time-setup" element={<PrivateRoute element={<TransportTimeSetupPage />} />} />
        <Route path="/fleet/setup" element={<PrivateRoute element={<TransportSetupPage />} />} />
        <Route path="/fleet/learners" element={<PrivateRoute element={<LearnerRoutes />} />} />
        <Route path="/fleet/grouping" element={<PrivateRoute element={<TransportGroupingBoard />} />} />
        <Route path="/fleet/manifest" element={<PrivateRoute element={<BusManifest />} />} />

        {/* Library */}
        <Route path="/library" element={<PrivateRoute element={<LibraryDashboard />} />} />
        <Route path="/library/catalog" element={<PrivateRoute element={<LibraryCatalog />} />} />
        <Route path="/library/storage-areas" element={<PrivateRoute element={<StorageAreaList />} />} />
        <Route path="/library/loans" element={<PrivateRoute element={<BookLoansPage />} />} />
        <Route path="/library/overdue" element={<PrivateRoute element={<OverdueBooks />} />} />
        <Route path="/library/bulk-assign" element={<PrivateRoute element={<BulkTextbookAssign />} />} />

        {/* Receivables */}
        <Route path="/learners" element={<PrivateRoute element={<LearnersList />} />} />
        <Route path="/statements" element={<PrivateRoute element={<StatementView />} />} />
        <Route path="/finance/balances" element={<PrivateRoute element={<LearnerBalances />} />} />
        <Route path="/payments" element={<PrivateRoute element={<PaymentUpload />} />} />
        <Route path="/billing" element={<PrivateRoute element={<BillingForm />} />} />
        <Route path="/payments/receipt" element={<PrivateRoute element={<PaymentReceiptForm />} />} />
        <Route path="/ledger/:admNo" element={<PrivateRoute element={<LearnerLedger />} />} />
        <Route path="/invoices" element={<PrivateRoute element={<InvoicesList />} />} />
        <Route path="/receipts" element={<PrivateRoute element={<ReceiptsList />} />} />
        <Route path="/finance" element={<FinanceDashboard />} />
        <Route path="/feestructures" element={<PrivateRoute element={<FeeStructureTable />} />} />

        {/* Shared */}
        <Route path="/banks" element={<BankList />} />
        <Route path="/banks/:accountNo" element={<BankLedger />} />
        <Route path="/gl-accounts" element={<GLAccountList />} />
        <Route path="/finance/general-journal" element={<PrivateRoute element={<GeneralJournal />} />} />
        <Route path="/setups/dimensions" element={<PrivateRoute element={<DimensionsList />} />} />
        <Route path="/finance/gl-by-dimension" element={<PrivateRoute element={<DimensionAnalysis />} />} />
        <Route path="/finance/pnl-by-dimension" element={<PrivateRoute element={<PnLByDimension />} />} />
        <Route path="/finance/trial-balance" element={<PrivateRoute element={<TrialBalance />} />} />
        <Route path="/finance/income-statement" element={<PrivateRoute element={<IncomeStatement />} />} />
        <Route path="/finance/find-entries" element={<PrivateRoute element={<FindEntries />} />} />
        <Route path="/finance/balance-sheet" element={<PrivateRoute element={<BalanceSheet />} />} />
        <Route path="/finance/vat-return" element={<PrivateRoute element={<VatReturn />} />} />
        <Route path="/finance/aged-receivables" element={<PrivateRoute element={<AgedReceivables />} />} />
        <Route path="/finance/collections" element={<PrivateRoute element={<CollectionsReport />} />} />
        <Route path="/finance/cash-flow" element={<PrivateRoute element={<CashFlow />} />} />
        <Route path="/finance/bank-reconciliation" element={<PrivateRoute element={<BankReconciliation />} />} />
        <Route path="/finance/credit-notes" element={<PrivateRoute element={<CreditNoteForm />} />} />
        <Route path="/setups/posting-period" element={<PrivateRoute element={<PostingPeriod />} />} />
        <Route path="/finance/budgets" element={<PrivateRoute element={<BudgetEntry />} />} />
        <Route path="/finance/budget-vs-actual" element={<PrivateRoute element={<BudgetVsActual />} />} />
        <Route path="/payables/credit-notes" element={<PrivateRoute element={<SupplierCreditNoteForm />} />} />
        <Route path="/finance/wht" element={<PrivateRoute element={<WhtReport />} />} />
        <Route path="/finance/year-end-close" element={<PrivateRoute element={<YearEndClose />} />} />
        <Route path="/gl-accounts/:accountNo" element={<GLEntries />} />

        {/* Payables */}
        <Route path="/suppliers" element={<PrivateRoute element={<SupplierList />} />} />
        <Route path="/suppliers/:supplierCode/ledger" element={<PrivateRoute element={<SupplierLedger />} />} />
        <Route path="/payables" element={<PrivateRoute element={<PayablesDashboard />} />} />
        <Route path="/payables/items" element={<PrivateRoute element={<ItemList />} />} />
        <Route path="/payables/purchase-requisitions" element={<PrivateRoute element={<PurchaseRequisitionList />} />} />
        <Route path="/payables/quotes" element={<PrivateRoute element={<PurchaseQuoteList />} />} />
        <Route path="/payables/quotes/new" element={<PrivateRoute element={<PurchaseQuoteForm />} />} />
        <Route path="/payables/quotes/:quoteNo/edit" element={<PrivateRoute element={<PurchaseQuoteForm />} />} />
        <Route path="/payables/orders" element={<PrivateRoute element={<PurchaseOrderList />} />} />
        <Route path="/payables/orders/new" element={<PrivateRoute element={<PurchaseOrderForm />} />} />
        <Route path="/payables/orders/:orderNo/edit" element={<PrivateRoute element={<PurchaseOrderForm />} />} />
        <Route path="/payables/orders/:orderNo/print" element={<PrivateRoute element={<PurchaseOrderPrint />} />} />
        <Route path="/payables/receipts" element={<PrivateRoute element={<PurchaseReceiptList />} />} />
        <Route path="/payables/vat-return" element={<PrivateRoute element={<VATReturnReport />} />} />
        <Route path="/payables/inventory/valuation" element={<PrivateRoute element={<InventoryValuation />} />} />
        <Route path="/payables/inventory/aging" element={<PrivateRoute element={<InventoryAging />} />} />
        <Route path="/payables/inventory/reorder" element={<PrivateRoute element={<InventoryReorder />} />} />
        <Route path="/payables/aging" element={<PrivateRoute element={<PayablesAging />} />} />
        <Route path="/payables/invoices" element={<PrivateRoute element={<PurchaseInvoiceList />} />} />
        <Route path="/payables/invoices/new" element={<PrivateRoute element={<PurchaseInvoiceForm />} />} />
        <Route path="/payables/vouchers" element={<PrivateRoute element={<PaymentVoucherList />} />} />
        <Route path="/payables/vouchers/new" element={<PrivateRoute element={<PaymentVoucherForm />} />} />
        <Route path="/payables/payment-methods" element={<PrivateRoute element={<PaymentMethodList />} />} />
        <Route path="/payables/items/:itemNo/ledger" element={<PrivateRoute element={<ItemLedgerEntries />} />} />
        <Route path="/payables/requisitions" element={<PrivateRoute element={<StoreRequisitionList />} />} />
        <Route path="/payables/requisitions/new" element={<PrivateRoute element={<StoreRequisitionForm />} />} />
        <Route path="/payables/requisitions/:reqNo/edit" element={<PrivateRoute element={<StoreRequisitionForm />} />} />
        <Route path="/payables/item-journals" element={<PrivateRoute element={<ItemJournalList />} />} />
        <Route path="/payables/item-journals/new" element={<PrivateRoute element={<ItemJournalForm />} />} />
        <Route path="/payables/item-journals/:journalNo/edit" element={<PrivateRoute element={<ItemJournalForm />} />} />

        {/* Procurement */}
        <Route path="/procurement" element={<PrivateRoute element={<ProcurementDashboard />} />} />
        <Route path="/procurement/transfers" element={<PrivateRoute element={<TransferOrderList />} />} />
        <Route path="/procurement/transfers/new" element={<PrivateRoute element={<TransferOrderForm />} />} />
        <Route path="/procurement/transfers/:transferNo/edit" element={<PrivateRoute element={<TransferOrderForm />} />} />

        {/* Fixed Assets */}
        <Route path="/fixed-assets" element={<PrivateRoute element={<FixedAssetList />} />} />
        <Route path="/fixed-assets/posting-groups" element={<PrivateRoute element={<FAPostingGroupList />} />} />
        <Route path="/fixed-assets/classes" element={<PrivateRoute element={<FAClassList />} />} />
        <Route path="/fixed-assets/:faNo/ledger" element={<PrivateRoute element={<FALedgerEntries />} />} />

        {/* Academics */}
        <Route path="/academics" element={<PrivateRoute element={<AcademicsDashboard />} />} />
        <Route path="/academics/years" element={<PrivateRoute element={<AcademicYearList />} />} />
        <Route path="/academics/stages" element={<PrivateRoute element={<StageList />} />} />
        <Route path="/academics/learning-areas" element={<PrivateRoute element={<LearningAreaList />} />} />
        <Route path="/academics/fee-templates" element={<PrivateRoute element={<FeeTemplateList />} />} />
        <Route path="/academics/stage-subjects" element={<PrivateRoute element={<StageLearningAreaList />} />} />
        <Route path="/academics/subject-teachers" element={<PrivateRoute element={<SubjectTeacherList />} />} />
        <Route path="/academics/timetable-setup" element={<PrivateRoute element={<TimetableSetup />} />} />
        <Route path="/academics/teacher-allocations" element={<PrivateRoute element={<TeacherAllocations />} />} />
        <Route path="/academics/timetable-grid" element={<PrivateRoute element={<TimetableGrid />} />} />
        <Route path="/academics/timetable-print" element={<PrivateRoute element={<TimetablePrint />} />} />
        <Route path="/academics/streams" element={<PrivateRoute element={<StreamList />} />} />
        <Route path="/academics/classes" element={<PrivateRoute element={<ClassList />} />} />
        <Route path="/academics/charge-items" element={<PrivateRoute element={<ChargeItemList />} />} />
        <Route path="/academics/events" element={<PrivateRoute element={<EventsList />} />} />

        {/* Role Center Dashboards */}
        <Route path="/supply-chain"  element={<PrivateRoute element={<SupplyChainDashboard />} />} />
        <Route path="/hr"            element={<PrivateRoute element={<HRDashboard />} />} />
        <Route path="/hr/employees"  element={<PrivateRoute element={<EmployeeList />} />} />
        <Route path="/hr/employees/:employeeNo" element={<PrivateRoute element={<EmployeeCard />} />} />
        <Route path="/hr/departments" element={<PrivateRoute element={<DepartmentList />} />} />
        <Route path="/hr/appraisal"         element={<PrivateRoute element={<AppraisalList />} />} />
        <Route path="/hr/appraisal/setup"   element={<PrivateRoute element={<AppraisalSetup />} />} />
        <Route path="/hr/appraisal/reports" element={<PrivateRoute element={<AppraisalReports />} />} />
        <Route path="/hr/disciplinary"         element={<PrivateRoute element={<DisciplinaryList />} />} />
        <Route path="/hr/disciplinary/reports" element={<PrivateRoute element={<DisciplinaryReports />} />} />
        <Route path="/hr/exit"         element={<PrivateRoute element={<ExitList />} />} />
        <Route path="/hr/exit/reports" element={<PrivateRoute element={<ExitReports />} />} />
        <Route path="/hr/pay-grades"  element={<PrivateRoute element={<PayGradeList />} />} />
        <Route path="/hr/pay-groups"  element={<PrivateRoute element={<PayGroupList />} />} />
        <Route path="/hr/leave"                        element={<PrivateRoute element={<LeaveDashboard />} />} />
        <Route path="/hr/leave/employees"              element={<PrivateRoute element={<LeaveEmployeeList />} />} />
        <Route path="/hr/leave/types"                  element={<PrivateRoute element={<LeaveTypeList />} />} />
        <Route path="/hr/leave/groups"                 element={<PrivateRoute element={<LeaveGroupList />} />} />
        <Route path="/hr/leave/applications"           element={<PrivateRoute element={<LeaveApplicationList />} />} />
        <Route path="/hr/leave/applications/new"       element={<PrivateRoute element={<LeaveApplicationForm />} />} />
        <Route path="/hr/leave/ledger"                 element={<PrivateRoute element={<LeaveLedgerPage />} />} />
        <Route path="/hr/leave/statement"              element={<PrivateRoute element={<LeaveStatementPage />} />} />
        <Route path="/hr/leave/report"                 element={<PrivateRoute element={<LeaveReportPage />} />} />
        <Route path="/payroll"                                   element={<PrivateRoute element={<PayrollDashboard />} />} />
        <Route path="/payroll/setup"                             element={<PrivateRoute element={<PayrollSetupPage />} />} />
        <Route path="/payroll/setup/transactions"                element={<PrivateRoute element={<TransactionCodeList />} />} />
        <Route path="/payroll/periods"                           element={<PrivateRoute element={<PayrollPeriodList />} />} />
        <Route path="/payroll/runs"                              element={<PrivateRoute element={<PayrollRunList />} />} />
        <Route path="/payroll/runs/:runNo"                       element={<PrivateRoute element={<PayrollRunCard />} />} />
        <Route path="/payroll/runs/:runNo/payslip/:employeeNo"   element={<PrivateRoute element={<PayslipView />} />} />
        <Route path="/payroll/reports"                           element={<PrivateRoute element={<PayrollReportPage />} />} />
        <Route path="/payroll/employees"                         element={<PrivateRoute element={<PayrollEmployeeList />} />} />

        {/* Setups */}
        <Route path="/setups" element={<PrivateRoute element={<SetupsList />} />} />
        <Route path="/setups/company" element={<PrivateRoute element={<CompanyDetailsPage />} />} />
        <Route path="/setups/mail" element={<PrivateRoute element={<MailSetupPage />} />} />
        <Route path="/setups/currencies" element={<PrivateRoute element={<CurrencyList />} />} />
        <Route path="/setups/number-series" element={<PrivateRoute element={<NumberSeriesList />} />} />
        <Route path="/setups/locations" element={<PrivateRoute element={<LocationList />} />} />
        <Route path="/setups/posting" element={<PrivateRoute element={<PostingSetupPage />} />} />
        <Route path="/setups/posting-groups" element={<PrivateRoute element={<PostingGroupsPage />} />} />
        <Route path="/setups/learner-posting-groups" element={<PrivateRoute element={<LearnerPostingGroupList />} />} />
        <Route path="/setups/vat-posting-groups"     element={<PrivateRoute element={<VATPostingGroupList />} />} />
        <Route path="/setups/grading-systems"        element={<PrivateRoute element={<GradingSystemsPage />} />} />
        <Route path="/setups/grading-scale"          element={<PrivateRoute element={<GradingScalePage />} />} />
        <Route path="/setups/users"                  element={<PrivateRoute element={<UserList />} />} />
        <Route path="/setups/user-groups"            element={<PrivateRoute element={<UserGroupList />} />} />
        <Route path="/setups/permission-sets"        element={<PrivateRoute element={<PermissionSetList />} />} />
        <Route path="/setups/data-import"            element={<PrivateRoute element={<DataImportPage />} />} />
        <Route path="/setups/approvals"              element={<PrivateRoute element={<ApprovalWorkflowSetup />} />} />
        <Route path="/approvals"                     element={<PrivateRoute element={<ApprovalsInbox />} />} />
        <Route path="/academics/periodic-activities" element={<PrivateRoute element={<PeriodicActivitiesPage />} />} />
        <Route path="/academics/assessment-setup"    element={<PrivateRoute element={<AssessmentSetupList />} />} />
        <Route path="/academics/score-entry"         element={<PrivateRoute element={<ScoreEntryPage />} />} />
        <Route path="/academics/assessment-report"   element={<PrivateRoute element={<AssessmentReportPage />} />} />
        <Route path="/academics/merit-list"           element={<PrivateRoute element={<MeritList />} />} />
        <Route path="/academics/report-forms"         element={<PrivateRoute element={<ClassReportForms />} />} />
        <Route path="/academics/pathway-insight"      element={<PrivateRoute element={<PathwayInsight />} />} />
        <Route path="/academics/lernalytics"          element={<PrivateRoute element={<LernAlytics />} />} />
        <Route path="/class-teacher-centre"          element={<PrivateRoute element={<ClassTeacherCentre />} />} />
        <Route path="/finance/batch-receipts" element={<PrivateRoute element={<BatchReceiptList />} />} />
        <Route path="/finance/batch-receipts/new" element={<PrivateRoute element={<BatchReceiptForm />} />} />

        {/* Parent Portal */}
        <Route path="/parent" element={<ParentRoute element={<ParentDashboard />} />} />
        <Route path="/parent/learners/:admNo" element={<ParentRoute element={<ParentLearnerProfile />} />} />
        <Route path="/parent/fees" element={<ParentRoute element={<ParentFees />} />} />
        <Route path="/parent/statements" element={<ParentRoute element={<ParentStatements />} />} />
        <Route path="/parent/academics" element={<ParentRoute element={<ParentAcademics />} />} />
        <Route path="/parent/assessments" element={<ParentRoute element={<ParentAssessments />} />} />
        <Route path="/parent/lernalytics" element={<ParentRoute element={<ParentLernAlytics />} />} />
        <Route path="/parent/teachers" element={<ParentRoute element={<ParentClassTeachers />} />} />
        <Route path="/parent/messages" element={<ParentRoute element={<ParentMessaging />} />} />
        <Route path="/parent/transport" element={<ParentRoute element={<ParentTransport />} />} />
        <Route path="/parent/books" element={<ParentRoute element={<ParentBooks />} />} />

        {/* Staff Portal — default self-service role centre for employees */}
        <Route path="/staff" element={<StaffRoute element={<StaffDashboard />} />} />
        <Route path="/staff/leave" element={<StaffRoute element={<StaffLeave />} />} />
        <Route path="/staff/timetable" element={<StaffRoute element={<StaffTimetable />} />} />
        <Route path="/staff/teaching" element={<StaffRoute element={<StaffTeaching />} />} />
        <Route path="/staff/requisitions" element={<StaffRoute element={<StaffRequisitions />} />} />
        <Route path="/staff/messages" element={<StaffRoute element={<ParentMessaging />} />} />
        <Route path="/staff/activities" element={<StaffRoute element={<StaffComingSoon title="My Activities" blurb="A personal view of your school activities and duties is on the way." />} />} />
        <Route path="/staff/imprest" element={<StaffRoute element={<StaffComingSoon title="Imprest" blurb="Request and track imprest advances. This module is coming soon." />} />} />
        <Route path="/staff/claims" element={<StaffRoute element={<StaffComingSoon title="Claim Requisitions" blurb="File and track expense claims. This module is coming soon." />} />} />
        <Route path="/staff/surrenders" element={<StaffRoute element={<StaffComingSoon title="Imprest Surrenders" blurb="Surrender and account for imprest advances. This module is coming soon." />} />} />

        {/* Staff/teacher mailbox — same email-scoped messaging UI, staff layout */}
        <Route path="/messages" element={<PrivateRoute element={<ParentMessaging />} />} />

        <Route path="/" element={<Landing />} />
      </Routes>
      </FinanceShell>
    </Router>
  );
}

export default App;
